import React, { useEffect } from 'react';
import { Box, InputAdornment, TextField } from '@mui/material';
import { useForm } from '@refinedev/react-hook-form';
import { SearchOutlined } from '@mui/icons-material';
import { HttpError } from '@refinedev/core';
import { useDebounce } from '@/hooks/useDebounce';
import { useTranslation } from 'react-i18next';

export type ParticipantFilters = {
  email?: string;
};

type ParticipantsFiltersProps = {
  onSearch: (filters: ParticipantFilters) => void;
  defaultFilters?: ParticipantFilters;
  debounceMs?: number;
};

export const ParticipantsFilters: React.FC<ParticipantsFiltersProps> = ({
  onSearch,
  defaultFilters = {},
  debounceMs = 300,
}) => {
  const { t } = useTranslation();
  const { register, handleSubmit, subscribe } = useForm<
    ParticipantFilters,
    HttpError
  >({
    defaultValues: defaultFilters,
    warnWhenUnsavedChanges: false,
  });

  const debouncedSearch = useDebounce(onSearch, debounceMs);

  useEffect(() => {
    const unsubscribe = subscribe({
      formState: { values: true },
      callback: ({ values }) => {
        debouncedSearch(values as ParticipantFilters);
      },
    });
    return () => unsubscribe();
  }, [subscribe, debouncedSearch]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSearch)}
      sx={{ mb: 2, pt: 1, maxWidth: 300 }}
    >
      <TextField
        {...register('email')}
        label={t('pages.participants.filters.email')}
        size="small"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlined />
              </InputAdornment>
            ),
          },
        }}
      />
    </Box>
  );
};
